import { useQuery } from '@apollo/client';
import { faExclamation } from '@fortawesome/free-solid-svg-icons/faExclamation';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Container, Header, Link, Loader, Message, Text } from '@gasbuddy/react-components';
import { SocialIcons } from '@gasbuddy/react-consumer-components';
import classnames from 'classnames/bind';
import gql from 'graphql-tag';
import PropTypes from 'prop-types';
import { Fragment } from 'react';
import { Helmet } from 'react-helmet-async';
import { Trans, useTranslation } from 'react-i18next';
import useImageSrcBuilder from '../../../lib/hooks/useImageSrcBuilder';
import { interpolateImageList } from '../../../lib/utils';
import buildTranslatedCMSDataObject from '../../../lib/utils/buildTranslatedCMSDataObject';
import generateCMSMetaTags from '../../../lib/utils/generateCMSMetaTags';
import { buildSlugArticlePath, getNewsroomFormattedDate } from '../../../lib/utils/newsroom';
import { SUPPORTED_LANGUAGES } from '../../constants';
import Breadcrumb from '../Breadcrumb';
import Markdown from '../Markdown';
import Markup from '../Markup';
import Page from '../Page';
import styles from './PressReleasePage.module.css';

const cx = classnames.bind(styles);

// expressions used to extract image elements from the article body
const imgRegex = /<img[^>]*>/g;
const srcRegex = /src\s*=\s*["'](.+?)["']/g;

export const GET_PRESS_RELEASE = gql`
  query Articles($identifier: ID!) {
    pressRelease(identifier: $identifier) {
      articleImages {
        alt
        image {
          height
          url
          width
        }
      }
      date
      headline
      htmlContent
      legacyId
      locales {
        fr {
          articleImages {
            alt
            image {
              height
              url
              width
            }
          }
          headline
          htmlContent
          markdownContent
          meta {
            content
            description
            image {
              height
              url
              width
            }
            name
            provider
            title
            type
          }
          title
        }
      }
      markdownContent
      meta {
        content
        description
        image {
          height
          url
          width
        }
        name
        provider
        title
        type
      }
      title
    }
  }
`;

function PressReleasePage({ match }) {
  const { t, i18n } = useTranslation(['article', 'miscellaneous']);
  const { data, error, loading } = useQuery(GET_PRESS_RELEASE, {
    variables: { identifier: match.params.identifier },
  });

  // Build the localized version of the press release if it exists
  const pressRelease = buildTranslatedCMSDataObject(data?.pressRelease, i18n.language);

  const imageToSrc = useImageSrcBuilder();
  const articleContent = pressRelease?.htmlContent || pressRelease?.markdownContent;
  const htmlImgArray = [];
  const imgContentMatch = articleContent?.match(imgRegex);

  const { articleImages } = pressRelease || {};
  const formattedImages = articleImages?.map(({ alt, image }) => ({
    url: imageToSrc(image),
    alt,
  })) || [];

  // need to still grab potential images from the article content for older press releases
  if (imgContentMatch) {
    imgContentMatch.map((imgElement) => {
      htmlImgArray.push(srcRegex.exec(imgElement)[1]);
      // reset last index
      srcRegex.lastIndex = 0;
      return imgElement;
    });
  }

  let articleSchema = {};

  if (pressRelease) {
    articleSchema = {
      '@type': 'NewsArticle',
      url: `https://www.gasbuddy.com${match.url}`,
      publisher: {
        '@type': 'Organization',
        name: 'GasBuddy',
        logo: 'https://static.gasbuddy.com/web/gas-buddy-logo-midnight-txt.svg',
      },
      headline: pressRelease.headline,
      mainEntityOfPage: `https://www.gasbuddy.com${match.url}`,
      articleBody: pressRelease.htmlContent || pressRelease.markdownContent,
      image: {
        '@id': htmlImgArray[0],
      },
      datePublished: pressRelease.date,
    };
  }

  return (
    <Page compact name="PressRelease" schema={articleSchema}>
      {!!pressRelease && (
        <Helmet>
          <title>{pressRelease.title || pressRelease.headline}</title>
          <link rel="canonical" href={`https://www.gasbuddy.com${buildSlugArticlePath(pressRelease)}`} />
          {!!pressRelease.meta?.length && generateCMSMetaTags(pressRelease.meta, imageToSrc)}
        </Helmet>
      )}
      <Container className={cx('container')}>
        <div className={cx('breadcrumbs')}>
          <Breadcrumb linkTo="/" showSeparator={false}>
            {t('navigation.home', { ns: 'miscellaneous' })}
          </Breadcrumb>
          <Breadcrumb linkTo="/newsroom">
            {t('newsroom', { ns: 'article' })}
          </Breadcrumb>
          <Breadcrumb linkTo="/newsroom/pressreleases">
            {t('pressReleases', { ns: 'article' })}
          </Breadcrumb>
        </div>
        {loading && (
          <Loader />
        )}
        {!!error && !pressRelease && (
          <Text as="p" data-testid="genericError">
            <Trans i18nKey="article:errorOccurredGoBackToNewsroom">
              An error has occurred. Please try again later or <Link to="/newsroom">click here</Link> to go back to the Newsroom.
            </Trans>
          </Text>
        )}
        {!error && !loading && !pressRelease && (
          <Text as="p">
            {t('unableToFindPressRelease', { ns: 'article' })}
            <br />
            <Trans i18nKey="article:goBackToNewsroom">
              Please <Link to="/newsroom">click here</Link> to go back to the Newsroom.
            </Trans>
          </Text>
        )}
        {!error && !loading && i18n.language === SUPPORTED_LANGUAGES.FRENCH && data?.pressRelease && !data.pressRelease.locales?.fr?.headline && (
          <Message color="gold" icon={<FontAwesomeIcon icon={faExclamation} />}>
            {t('missingTranslation', { ns: 'article' })}
          </Message>
        )}
        {!!pressRelease && (
          <Fragment>
            <Header className={cx('header')} size="sm">{pressRelease.headline}</Header>
            <div className={cx('date')}>{getNewsroomFormattedDate(pressRelease.date)}</div>
            <article>
              {pressRelease.htmlContent
                ? <Markup source={interpolateImageList(pressRelease.htmlContent, formattedImages)} />
                : <Markdown>{interpolateImageList(pressRelease.markdownContent, formattedImages)}</Markdown>
              }
            </article>
            <div className={cx('social')}>
              <Text as="p" bold size="lg" uppercase>
                {t('actions.share', { ns: 'miscellaneous' })}
              </Text>
              <SocialIcons className={cx('socialIcons')} />
            </div>
          </Fragment>
        )}
      </Container>
    </Page>
  );
}

PressReleasePage.propTypes = {
  match: PropTypes.shape({
    url: PropTypes.string,
    params: PropTypes.shape({
      name: PropTypes.string,
      identifier: PropTypes.string,
    }),
  }),
};

PressReleasePage.defaultProps = {
  match: {
    params: {},
  },
};

export default PressReleasePage;
